<template>
  <canvas ref="canvas" class="particle-field" aria-hidden="true"></canvas>
</template>

<script>
/**
 * ParticleField — fundo de partículas interativas (estilo "constelação").
 * Pontos flutuam devagar; ao aproximar o mouse eles são repelidos e
 * ligam-se por linhas finas. Canvas puro, sem dependências.
 *
 * Performance / acessibilidade:
 *  - respeita prefers-reduced-motion (render estático, sem loop nem mouse)
 *  - pausa quando a aba fica oculta
 *  - densidade proporcional à área, com teto máximo
 */
export default {
  name: 'ParticleField',
  props: {
    color: { type: String, default: '120, 170, 255' }, // rgb
    density: { type: Number, default: 0.00009 }, // partículas por px²
    maxParticles: { type: Number, default: 90 },
    linkDistance: { type: Number, default: 130 },
    mouseRadius: { type: Number, default: 150 },
  },
  data() {
    return {
      ctx: null,
      particles: [],
      mouse: { x: -9999, y: -9999 },
      raf: null,
      width: 0,
      height: 0,
      dpr: 1,
      reduced: false,
    };
  },
  mounted() {
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canvas = this.$refs.canvas;
    this.ctx = canvas.getContext('2d');
    this.resize();
    window.addEventListener('resize', this.resize);
    if (!this.reduced) {
      window.addEventListener('mousemove', this.onMouseMove, { passive: true });
      window.addEventListener('mouseout', this.onMouseOut, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibility);
      this.loop();
    } else {
      this.draw(); // frame estático
    }
  },
  beforeUnmount() {
    cancelAnimationFrame(this.raf);
    window.removeEventListener('resize', this.resize);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseout', this.onMouseOut);
    document.removeEventListener('visibilitychange', this.onVisibility);
  },
  methods: {
    resize() {
      const canvas = this.$refs.canvas;
      const parent = canvas.parentElement;
      this.dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.width = parent.clientWidth;
      this.height = parent.clientHeight;
      canvas.width = this.width * this.dpr;
      canvas.height = this.height * this.dpr;
      canvas.style.width = this.width + 'px';
      canvas.style.height = this.height + 'px';
      this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      this.initParticles();
      if (this.reduced) this.draw();
    },
    initParticles() {
      const count = Math.min(
        Math.floor(this.width * this.height * this.density),
        this.maxParticles
      );
      this.particles = Array.from({ length: count }, () => ({
        x: Math.random() * this.width,
        y: Math.random() * this.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 1.6 + 0.8,
      }));
    },
    onMouseMove(e) {
      const rect = this.$refs.canvas.getBoundingClientRect();
      this.mouse.x = e.clientX - rect.left;
      this.mouse.y = e.clientY - rect.top;
    },
    onMouseOut() {
      this.mouse.x = -9999;
      this.mouse.y = -9999;
    },
    onVisibility() {
      if (document.hidden) {
        cancelAnimationFrame(this.raf);
        this.raf = null;
      } else if (!this.raf && !this.reduced) {
        this.loop();
      }
    },
    loop() {
      this.update();
      this.draw();
      this.raf = requestAnimationFrame(this.loop);
    },
    update() {
      const { width: w, height: h, mouseRadius: mr } = this;
      for (const p of this.particles) {
        p.x += p.vx;
        p.y += p.vy;
        // wrap nas bordas
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;
        // repulsão do mouse
        const dx = p.x - this.mouse.x;
        const dy = p.y - this.mouse.y;
        const dist = Math.hypot(dx, dy);
        if (dist < mr && dist > 0) {
          const force = (mr - dist) / mr;
          p.x += (dx / dist) * force * 2.2;
          p.y += (dy / dist) * force * 2.2;
        }
      }
    },
    draw() {
      const ctx = this.ctx;
      const { width: w, height: h, color, linkDistance: ld } = this;
      ctx.clearRect(0, 0, w, h);

      // linhas entre partículas próximas
      for (let i = 0; i < this.particles.length; i++) {
        const a = this.particles[i];
        for (let j = i + 1; j < this.particles.length; j++) {
          const b = this.particles[j];
          const dist = Math.hypot(a.x - b.x, a.y - b.y);
          if (dist < ld) {
            ctx.strokeStyle = `rgba(${color}, ${0.18 * (1 - dist / ld)})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
        // linha do mouse até partículas próximas (destaque)
        const md = Math.hypot(a.x - this.mouse.x, a.y - this.mouse.y);
        if (md < this.mouseRadius) {
          ctx.strokeStyle = `rgba(${color}, ${0.35 * (1 - md / this.mouseRadius)})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(this.mouse.x, this.mouse.y);
          ctx.stroke();
        }
      }

      // os pontos
      for (const p of this.particles) {
        ctx.fillStyle = `rgba(${color}, 0.7)`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
    },
  },
};
</script>

<style scoped>
.particle-field {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}
</style>
